// =============================================================================
// Per-page slice hooks (chat, intelligence, dashboard, charting) — the session-
// scoped view state each page wants to survive navigation, read via the shared
// pageStore. Each hook is built from the shared makeSliceHook factory; setters are
// module-level. See sliceHook.ts for the selector-stability rule.
// =============================================================================
import { makeSliceHook } from './sliceHook'
import { updateSlice, type ChatMessage, type Persona } from './pageStore'
import { setActor } from '../lib/api'

// ---- Ask Portal (AI Chat) ----------------------------------------------------
export const useAskPortalSlice = makeSliceHook('askPortal')
// Functional updater over LIVE store state (not a render snapshot), so a send()'s
// post-await append and a concurrent sendFeedback() can't clobber each other.
export const updateChatMessages = (fn: (prev: ChatMessage[]) => ChatMessage[]) =>
  updateSlice('askPortal', (s) => ({ ...s, messages: fn(s.messages) }))

// ---- Intelligence (NBA) ------------------------------------------------------
export const useIntelligenceSlice = makeSliceHook('intelligence')
export const setNba = (nba: unknown) => updateSlice('intelligence', (s) => ({ ...s, nba }))
export const setCardsExpanded = (cardsExpanded: boolean) => updateSlice('intelligence', (s) => ({ ...s, cardsExpanded }))

// ---- Dashboard ---------------------------------------------------------------
export const useDashboardSlice = makeSliceHook('dashboard')
export const setSelectedKpi = (selectedKpi: string) => updateSlice('dashboard', (s) => ({ ...s, selectedKpi }))

// ---- AI Charting -------------------------------------------------------------
export const useChartingSlice = makeSliceHook('charting')
export const setLastChart = (lastChart: unknown, lastPrompt: string) => updateSlice('charting', (s) => ({ ...s, lastChart, lastPrompt }))

// ---- Persona ("user view") ---------------------------------------------------
// Global identity: scopes the Sites list and tags edits. setPersona also pushes the
// name + role into the API client (module-level actor) so writes carry X-Actor-Name /
// X-Actor-Role, and the change log / Activity attribute the edit to the person AND role.
export const usePersonaSlice = makeSliceHook('persona')
export const setPersona = (persona: Persona) => {
  setActor(persona.name, persona.role)
  updateSlice('persona', (s) => (s.persona === persona ? s : { ...s, persona }))
}
